import { v4 as uuid } from 'uuid';

import { PartialRequired } from '@vimeo/core/types/generic';

import {
  EPollState,
  IChatMessageAuthorRecord,
  IChatMessageRecord,
  IPoll,
  IPollOption,
  IQnAQuestionAuthor,
  IQnAQuestionRecord,
  IQnAQuestionReplyRecord,
  IQnASessionRecord,
} from '../../types/interaction_tools';
import { omit } from '../general/object';
import { getAbsoluteNow } from '../general/time';
import {
  collectMessageAuthorInteractionErrors,
  collectMessageInteractionErrors,
  collectPollInteractionErrors,
  collectPollOptionInteractionErrors,
  collectQuestionAuthorInteractionErrors,
  collectQuestionInteractionErrors,
  collectQuestionReplyInteractionErrors,
  collectQuestionSessionInteractionErrors,
} from './interaction_objects.validators';
import { assertNoTypeErrors } from './validator_utils';

/**
 * Create poll object used by interaction tools.
 */
export function createPollInteractionObject(
  {
    id,
    createdAt = getAbsoluteNow(),
    updatedAt = 0,
    question,
    options,
    state = EPollState.DRAFT,
  }: PartialRequired<IPoll, 'id' | 'question' | 'options'>,
  { isDbModel, validate = true }: { isDbModel?: boolean; validate?: boolean } = {},
): IPoll {
  if (validate) {
    assertNoTypeErrors(
      'poll',
      collectPollInteractionErrors({ id, createdAt, question, options, state }),
    );
  }

  const poll: IPoll = {
    id,
    createdAt,
    updatedAt,
    question,
    options,
    state,
  };

  /**
   * If should match remote DB model, not local transformed representation.
   */
  if (isDbModel) {
    poll.options = poll.options.map((it) => omit(it, 'votesCount')) as Array<IPollOption>;
  }

  return poll;
}

/**
 * Create poll object vote item.
 */
export function createPollOptionInteractionObject(
  { id, text, correct = false, votesCount = 0 }: PartialRequired<IPollOption, 'id' | 'text'>,
  { validate = true }: { validate?: boolean } = {},
): IPollOption {
  if (validate) {
    assertNoTypeErrors(
      'poll option',
      collectPollOptionInteractionErrors({ id, text, votesCount, correct }),
    );
  }

  return {
    id,
    correct,
    votesCount,
    text,
  };
}

/**
 * Create question session used by interaction utils to indicate that QnA session is started.
 */
export function createQuestionSessionRecordInteractionObject({
  id,
  createdAt = getAbsoluteNow(),
  questions = {},
  startedAt = 0,
  isQnaModerated = false,
}: PartialRequired<IQnASessionRecord, 'id'>): IQnASessionRecord {
  const qnaSession: IQnASessionRecord = {
    id,
    createdAt,
    startedAt,
    isQnaModerated,
    questions,
  };

  if (isQnaModerated === true) {
    qnaSession.pendingQuestions = {};
  }

  const possibleErrors = collectQuestionSessionInteractionErrors(qnaSession);

  assertNoTypeErrors('question', possibleErrors);
  return qnaSession;
}

/**
 * Create question representation stored in DB record.
 */
export function createQuestionInteractionObject({
  id,
  anonymous,
  createdAt = getAbsoluteNow(),
  state,
  text,
  user,
}: PartialRequired<
  IQnAQuestionRecord,
  'id' | 'text' | 'anonymous' | 'state' | 'user'
>): IQnAQuestionRecord {
  const qnaQuestion: IQnAQuestionRecord = {
    id,
    anonymous,
    createdAt,
    state,
    text: text.trim(),
    user,
    votes: {},
  };

  assertNoTypeErrors('message question object', collectQuestionInteractionErrors(qnaQuestion));

  return qnaQuestion;
}

/**
 * Create question reply representation stored in DB record.
 */
export function createQuestionReplyInteractionObject({
  questionId,
  createdAt = getAbsoluteNow(),
  text,
  user,
}: PartialRequired<
  IQnAQuestionReplyRecord,
  'questionId' | 'text' | 'user'
>): IQnAQuestionReplyRecord {
  const qnaQuestionReply: IQnAQuestionReplyRecord = {
    questionId,
    createdAt,
    text: text.trim(),
    user,
    votes: {},
  };

  assertNoTypeErrors(
    'message question reply object',
    collectQuestionReplyInteractionErrors(qnaQuestionReply),
  );

  return qnaQuestionReply;
}

/**
 * Create question author representation stored in DB record.
 */
export function createQuestionAuthorInteractionObject({
  id,
  displayName,
  avatarUrl,
}: IQnAQuestionAuthor): IQnAQuestionAuthor {
  const user: IQnAQuestionAuthor = {
    id,
    displayName,
    avatarUrl,
  };

  assertNoTypeErrors(
    'question author representation',
    collectQuestionAuthorInteractionErrors(user),
  );

  return user;
}

/**
 * Create message author representation stored in DB record.
 */
export function createMessageAuthorInteractionObject({
  id,
  displayName,
  avatarUrl,
  isCreator,
}: PartialRequired<IChatMessageAuthorRecord, 'id' | 'displayName'>): IChatMessageAuthorRecord {
  const possibleErrors = collectMessageAuthorInteractionErrors({
    id,
    displayName,
    avatarUrl,
    isCreator,
  });

  assertNoTypeErrors('message author representation', possibleErrors);

  const user: IChatMessageAuthorRecord = {
    id,
    displayName,
    avatarUrl,
  };

  /**
   * Include isCreator flag only if it is creator.
   * Omit optional fields to prevent garbage collection.
   */
  if (isCreator) {
    user.isCreator = true;
  }

  return user;
}

/**
 * @returns default empty poll used for forms init
 */
export function createDefaultPollInteractionObject(): IPoll {
  return createPollInteractionObject(
    {
      id: '',
      question: '',
      state: EPollState.DRAFT,
      createdAt: 0,
      options: [
        createPollOptionInteractionObject(
          {
            id: uuid(),
            text: '',
          },
          { validate: false },
        ),
        createPollOptionInteractionObject(
          {
            id: uuid(),
            text: '',
          },
          { validate: false },
        ),
      ],
    },
    { validate: false },
  );
}

/**
 * Create message representation stored in DB record.
 * ID is not included by rules and expected to be auto-injected.
 */
export function createMessageInteractionObject({
  contents,
  createdAt = getAbsoluteNow(),
  user,
}: PartialRequired<IChatMessageRecord, 'contents' | 'user'>): IChatMessageRecord {
  const possibleErrors = collectMessageInteractionErrors({
    contents,
    createdAt,
    user,
  });

  assertNoTypeErrors('message interaction object', possibleErrors);

  return {
    contents: contents.trim(),
    createdAt,
    user,
  };
}
